<template>
  <div class="financial-plan-table table">
    <div class="thead">
      <div class="tr">
        <div class="th !flex-[5.2] !font-bold">
          R'000
        </div>

        <div
          v-for="year in years"
          :key="year.key"
          class="th"
        >
          {{ year.label }}
        </div>
      </div>
    </div>

    <div class="tbody">
      <template
        v-for="(row, rowIndex) in tableRows"
        :key="rowIndex"
      >
        <div
          v-if="!isSpacer(row.type)"
          class="tr"
          :class="row.type"
        >
          <div class="td !flex-[5.2]">
            {{ row.label }}
          </div>

          <div
            v-for="year in years"
            :key="year.key"
            class="td"
          >
            <template v-if="!isHeader(row.type)">
              {{ formatValue(year.key, row) }}
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStringFormatter } from '@/composables';
import { mtrefYearHeadings, rowDefinitions } from '@/const';

const { financialTableFormatters } = useStringFormatter();

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  storeKey: {
    type: String,
    required: true,
  },
});

const years = mtrefYearHeadings.map((label, index) => ({ key: `year${index + 1}`, label }));

const tableRows = computed(() => rowDefinitions[props.storeKey] || []);

const isInputRow = (type) => ['value', 'italic-value', 'bold-value', 'no-indent-italic-value', 'bold-italic-value'].includes(type);

const isSpacer = (type) => ['empty'].includes(type);
const isHeader = (type) => ['underlined-header', 'uppercase-header', 'subheader'].includes(type);

const getValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const formatValue = (year, row) => {
  let val;
  if (isInputRow(row.type)) {
    const root = props.plan.content.financialPlan[props.storeKey]?.[year];
    val = getValue(root, row.path);
  } else {
    // Only fetch computed if it's not an input
    val = calculatedTotals.value[year]?.[row.path];
  }

  const formatted = (financialTableFormatters[row.format] || financialTableFormatters.default).format(val);
  // Zero and empty cells display as a dash in the finalised plan
  return formatted === '' || formatted === '0' ? '-' : formatted;
};

const calculatedTotals = computed(() => {
  const totals = { year1: {}, year2: {}, year3: {} };

  years.forEach(({ key: year }, i) => {
    const root = props.plan.content.financialPlan[props.storeKey]?.[year] || {};

    const get = (path) => {
      const v = getValue(root, path);
      // Ensure we treat empty strings or nulls as 0 for calculations
      return v && v !== '' ? parseFloat(v) : 0;
    };

    const set = (path, val) => { totals[year][path] = val; };

    if (props.storeKey === 'financialPerformanceProjection') {
      const rev = get('revenue.propertyRates') + get('revenue.serviceCharges') + get('revenue.investmentRevenue') + get('revenue.operationalTransfersAndSubsidies') + get('revenue.otherOwnRevenue');
      set('totalRevenue', rev);

      const exp = get('expenditure.employeeCosts') + get('expenditure.councillorRemuneration') + get('expenditure.depreciationAndAmortisation') + get('expenditure.financeCharges') + get('expenditure.inventoryConsumed') + get('expenditure.transfersAndSubsidies') + get('expenditure.other');
      set('totalExpenditure', exp);

      const surplus = rev - exp;
      set('surplusDeficit', surplus);

      const capSurplus = surplus + get('monetaryTransfersAndSubsidies') + get('inkindTransfersAndSubsidies');
      set('surplusDeficitAfterCapital', capSurplus);
      set('surplusDeficitForYear', capSurplus + get('attributableShare'));
    }
    else if (props.storeKey === 'capitalExpenditureAndFinancing') {
      // 1. Calculate Sub-Groups first (Functional)
      const gov = get('municipalGovernanceAndAdministration.executiveAndCouncil') + get('municipalGovernanceAndAdministration.financeAndAdministration') + get('municipalGovernanceAndAdministration.internalAudit');
      set('subTotalGov', gov);

      const comm = get('communityAndPublicSafety.communityAndSocialServices') + get('communityAndPublicSafety.sportAndRecreation') + get('communityAndPublicSafety.publicSafety') + get('communityAndPublicSafety.housing') + get('communityAndPublicSafety.health');
      set('subTotalCommunity', comm);

      const eco = get('economicAndEnvironmentalServices.planningAndDevelopment') + get('economicAndEnvironmentalServices.roadTransport') + get('economicAndEnvironmentalServices.environmentalProtection');
      set('subTotalEconomic', eco);

      const trading = get('tradingServices.energySources') + get('tradingServices.waterAndManagement') + get('tradingServices.wasteWaterManagement') + get('tradingServices.wasteManagement');
      set('subTotalTrading', trading);

      // 2. Total Functional
      set('totalCapitalExpenditure', gov + comm + eco + trading + get('other'));

      // 3. Funding
      const transfers = get('funding.nationalGovernment') + get('funding.provincialGovernment') + get('funding.districtMunicipality') + get('funding.transfersAndSubsidies');
      set('totalCapitalTransfers', transfers);

      set('totalCapitalFunding', transfers + get('borrowing') + get('internallyGeneratedFunds'));
    }
    else if (props.storeKey === 'financialPositionProjection') {
      const curAss = get('currentAssets.cash') + get('currentAssets.shortTermInvestments') + get('currentAssets.exchangeTransactions') + get('currentAssets.nonexchangeTransactions') + get('currentAssets.nonCurrentReceivables') + get('currentAssets.inventory') + get('currentAssets.vat') + get('currentAssets.other');
      set('totalCurrentAssets', curAss);

      const nonCurAss = get('noncurrentAssets.investments') + get('noncurrentAssets.investmentProperty') + get('noncurrentAssets.propertyPlantAndEquipment') + get('noncurrentAssets.biologicalAssets') + get('noncurrentAssets.resources') + get('noncurrentAssets.heritageAssets') + get('noncurrentAssets.intangibleAssets') + get('noncurrentAssets.exchangeTransactions') + get('noncurrentAssets.nonexchangeTransactions') + get('noncurrentAssets.other');
      set('totalNonCurrentAssets', nonCurAss);

      const totAss = curAss + nonCurAss;
      set('totalAssets', totAss);

      // Include the 'liabilities' generic field in the sum if provided
      const curLiab = get('currentLiabilities.liabilities') + get('currentLiabilities.bankOverdraft') + get('currentLiabilities.financialLiabilities') + get('currentLiabilities.consumerDeposits') + get('currentLiabilities.exchangeTransactions') + get('currentLiabilities.nonexchangeTransactions') + get('currentLiabilities.provision') + get('currentLiabilities.vat') + get('currentLiabilities.other');
      set('totalCurrentLiabilities', curLiab);

      const nonCurLiab = get('noncurrentLiabilities.financialLiabilities') + get('noncurrentLiabilities.provision') + get('noncurrentLiabilities.longTermTradePayables') + get('noncurrentLiabilities.other');
      set('totalNonCurrentLiabilities', nonCurLiab);

      const totLiab = curLiab + nonCurLiab;
      set('totalLiabilities', totLiab);

      set('netAssets', totAss - totLiab);

      const equity = get('equity.accumulatedSurplusOrDeficit') + get('equity.reservesAndFunds') + get('equity.other');
      set('totalCommunityWealth', equity);
    }
    else if (props.storeKey === 'cashFlowProjection') {
      const opRec = get('operatingActivities.receipts.propertyRates') + get('operatingActivities.receipts.serviceCharges') + get('operatingActivities.receipts.otherRevenue') + get('operatingActivities.receipts.operationalTransfersAndSubsidies') + get('operatingActivities.receipts.capitalTransfersAndSubsidies') + get('operatingActivities.receipts.interest') + get('operatingActivities.receipts.dividends');
      const opPay = get('operatingActivities.payments.suppliersAndEmployees') + get('operatingActivities.payments.financeCharges') + get('operatingActivities.payments.transfersAndSubsidies');
      const netOp = opRec + opPay;
      set('netCashOperating', netOp);

      const invRec = get('investingActivities.receipts.ppeDisposalProceeds') + get('investingActivities.receipts.noncurrentDebtors') + get('investingActivities.receipts.noncurrentReceivables') + get('investingActivities.receipts.noncurrentInvestments') + get('investingActivities.receipts.insuranceRefundCapital') + get('investingActivities.receipts.shortLongTermInvestmentInterest');
      const invPay = get('investingActivities.payments.capitalAssets') + get('investingActivities.payments.retentionCapital');
      const netInv = invRec + invPay;
      set('netCashInvesting', netInv);

      const finRec = get('financingActivities.receipts.shortTermLoans') + get('financingActivities.receipts.longTermBorrowing') + get('financingActivities.receipts.consumerDeposits');
      const finPay = get('financingActivities.payments.borrowingRepayment');
      const netFin = finRec + finPay;
      set('netCashFinancing', netFin);

      const netTotal = netOp + netInv + netFin;
      set('netIncreaseDecrease', netTotal);

      const prevYear = i > 0 ? years[i - 1].key : null;
      const yearBeginCash = prevYear ? (totals[prevYear].yearEndCash || 0) : get('yearBeginCash');
      set('yearBeginCash', yearBeginCash);
      set('yearEndCash', yearBeginCash + netTotal);
    }
  });

  return totals;
});
</script>
